import { ensureFreshSession } from "@auth/refreshMiddleware";
import {
  createMicSource,
  MicrophoneError,
  type PcmSource,
} from "@transcription/pcmSource";
import {
  CAPTURE_SAMPLE_RATE,
  PlaybackQueue,
  VOICE_SAMPLE_RATE,
  decodeBase64Pcm16,
} from "@voice/audio";
import { useCallback, useEffect, useRef, useState } from "react";

import { getConfig } from "../config";

type VoiceStatus =
  | "idle"
  | "connecting"
  | "live"
  /** Voice agent disabled; terminal (`start()` refuses), so it latches the dictation fallback. */
  | "unavailable"
  | "error";

interface VoiceFrame {
  type?: string;
  payload?: string;
  message?: string;
}

/** Returned so callers fall back inline rather than setting state from an effect. */
type VoiceStartResult = "live" | "unavailable" | "error";

export interface UseVoiceSessionResult {
  status: VoiceStatus;
  startedAt: number | null;
  error: string | null;
  /** `error` came from local capture (`MicrophoneError`), not the socket or agent. */
  micFailure: boolean;
  speaking: boolean;
  start: () => Promise<VoiceStartResult>;
  stop: () => void;
  /** Kept on the session, which outlives the page: a dismissal in the control is lost on navigation. */
  ackError: () => void;
}

/** Mirrors the kill-switch error in brian `routes/voice.py`; matched exactly so a real failure isn't read as disabled. */
const UNAVAILABLE_MESSAGE = "voice unavailable";

export function useVoiceSession(): UseVoiceSessionResult {
  const wsUrl = `${getConfig().wsUrl}/ws/voice`;
  const [status, setStatus] = useState<VoiceStatus>("idle");
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [micFailure, setMicFailure] = useState(false);
  const [speaking, setSpeaking] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const sourceRef = useRef<PcmSource | null>(null);
  const playbackCtxRef = useRef<AudioContext | null>(null);
  const queueRef = useRef<PlaybackQueue | null>(null);
  const stoppingRef = useRef(false);
  const speakingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Cleared on fire so a later failure can't resolve an already-settled attempt.
  const settleRef = useRef<((result: VoiceStartResult) => void) | null>(null);

  const settle = useCallback((result: VoiceStartResult) => {
    const resolve = settleRef.current;
    settleRef.current = null;
    resolve?.(result);
  }, []);

  const cleanup = useCallback(() => {
    setStartedAt(null);
    if (speakingTimerRef.current !== null) {
      clearInterval(speakingTimerRef.current);
      speakingTimerRef.current = null;
    }
    sourceRef.current?.stop();
    sourceRef.current = null;
    queueRef.current?.flush();
    queueRef.current = null;
    playbackCtxRef.current?.close().catch(() => {});
    playbackCtxRef.current = null;
    setSpeaking(false);
    const ws = wsRef.current;
    wsRef.current = null;
    if (!ws) return;
    ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null;
    if (
      ws.readyState !== WebSocket.CLOSED &&
      ws.readyState !== WebSocket.CLOSING
    )
      ws.close();
  }, []);

  const handleFrame = useCallback(
    (raw: string) => {
      let frame: VoiceFrame;
      try {
        frame = JSON.parse(raw) as VoiceFrame;
      } catch {
        return;
      }
      if (frame.type === "audio" && typeof frame.payload === "string") {
        queueRef.current?.enqueue(decodeBase64Pcm16(frame.payload));
        return;
      }
      if (frame.type === "ended") {
        // Backend idle close (`IDLE_CLOSED_FRAME`); marked as our stop so the close isn't "Connection lost".
        // `warning` needs no handling: Brian says it aloud.
        stoppingRef.current = true;
        setStatus("idle");
        cleanup();
        return;
      }
      if (frame.type === "interrupted") {
        // Barge-in: the provider cut the reply but can't un-send audio already queued here.
        queueRef.current?.flush();
        setSpeaking(false);
        return;
      }
      if (frame.type === "error") {
        stoppingRef.current = true;
        if (frame.message === UNAVAILABLE_MESSAGE) {
          setStatus("unavailable");
          setError(null);
          settle("unavailable");
        } else {
          setStatus("error");
          setError(frame.message ?? "Voice session failed.");
          settle("error");
        }
        cleanup();
      }
    },
    [cleanup, settle],
  );

  const start = useCallback(async (): Promise<VoiceStartResult> => {
    if (wsRef.current || sourceRef.current) return "error";
    // Refused here so no caller opens a stray socket to re-probe the kill switch.
    if (status === "unavailable") return "unavailable";
    if (status === "connecting" || status === "live") return "error";
    setError(null);
    setMicFailure(false);
    stoppingRef.current = false;
    setStartedAt(null);
    setStatus("connecting");

    const token = (await ensureFreshSession())?.accessToken;
    if (!token) {
      setStatus("error");
      setError("Not authenticated");
      return "error";
    }

    const settled = new Promise<VoiceStartResult>((resolve) => {
      settleRef.current = resolve;
    });

    const ws = new WebSocket(wsUrl, ["scribenote.auth.bearer.v1", token]);
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;

    ws.onmessage = (ev) => {
      if (typeof ev.data === "string") handleFrame(ev.data);
    };
    // No reconnect: resuming a conversation mid-sentence without the provider's turn state is worse than ending it.
    ws.onerror = () => {
      if (stoppingRef.current) return;
      setStatus("error");
      setError("Connection lost");
      settle("error");
      cleanup();
    };
    ws.onclose = () => {
      // Settle unconditionally so a close without a report can't leave `start()` pending.
      if (stoppingRef.current) {
        settle("error");
        return;
      }
      setStatus("idle");
      settle("error");
      cleanup();
    };

    ws.onopen = () => {
      void (async () => {
        const playbackCtx = new AudioContext({ sampleRate: VOICE_SAMPLE_RATE });
        playbackCtxRef.current = playbackCtx;
        queueRef.current = new PlaybackQueue(playbackCtx, VOICE_SAMPLE_RATE);
        speakingTimerRef.current = setInterval(() => {
          setSpeaking(queueRef.current?.speaking ?? false);
        }, 200);

        const source = createMicSource({
          targetRate: VOICE_SAMPLE_RATE,
          contextSampleRate: CAPTURE_SAMPLE_RATE,
        });
        sourceRef.current = source;
        try {
          await source.start({
            emit: (chunk) => {
              if (ws.readyState === WebSocket.OPEN) ws.send(chunk);
            },
            whenReady: Promise.resolve(),
          });
        } catch (e) {
          // AudioContext or worklet fetch can fail with the mic fine; only `getUserMedia` throws MicrophoneError.
          stoppingRef.current = true;
          setStatus("error");
          setMicFailure(e instanceof MicrophoneError);
          setError(e instanceof Error ? e.message : "Audio capture failed.");
          settle("error");
          cleanup();
          return;
        }
        setStartedAt(Date.now());
        setStatus("live");
        settle("live");
      })();
    };

    return settled;
  }, [wsUrl, handleFrame, cleanup, settle, status]);

  const stop = useCallback(() => {
    stoppingRef.current = true;
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN)
      ws.send(JSON.stringify({ type: "stop" }));
    setStatus("idle");
    cleanup();
  }, [cleanup]);

  const ackError = useCallback(() => {
    setStatus((prev) => (prev === "error" ? "idle" : prev));
    setError(null);
    setMicFailure(false);
  }, []);

  useEffect(() => cleanup, [cleanup]);

  return {
    status,
    startedAt,
    error,
    micFailure,
    speaking,
    start,
    stop,
    ackError,
  };
}
